// 重複チェックダイアログ。
// 履歴の「重複チェック」ボタン押下時にオンデマンドで計算。
// 同じ取引先 × 同じ金額で、請求日が±7日以内 or 同対象月のグループを重複候補として抽出する。
// 定期取引は判定対象外（毎月同額が正常なため）。
// 2026-05-14 リファクタで src/pages/InputPage.tsx から分離。
import { useMemo } from 'react'
import { AlertTriangle, CheckCircle2, X, Edit2, Trash2 } from 'lucide-react'
import { DUE_DATE_COLUMN_LABEL } from '../../../utils/labels'
import type { LogEntry } from '../types'

interface Props {
  /** 履歴に表示中の取引一覧。ここから重複候補を抽出する */
  entries: LogEntry[]
  /** 検索期間（説明文表示用） */
  periodDays: number
  /** ダイアログを閉じる */
  onClose: () => void
  /** 編集ボタン押下時のコールバック（呼び出し側で対象を編集モードにする） */
  onEdit: (entry: LogEntry) => void
  /** 削除ボタン押下時のコールバック（呼び出し側で API 経由削除） */
  onDelete: (entry: LogEntry) => Promise<void> | void
}

export default function DuplicateCheckDialog({ entries, periodDays, onClose, onEdit, onDelete }: Props) {
  const duplicateGroups = useMemo<LogEntry[][]>(() => {
    const nonRecurring = entries.filter((e) => !e.isRecurring)
    // vendor × amount でグルーピング
    const byKey = new Map<string, LogEntry[]>()
    for (const e of nonRecurring) {
      const k = `${e.vendorId}_${e.amount}`
      if (!byKey.has(k)) byKey.set(k, [])
      byKey.get(k)!.push(e)
    }
    // 2件以上のグループから、更に「日付±7日以内 or 同対象月」で重複らしいもののみを抽出
    const groups: LogEntry[][] = []
    for (const group of byKey.values()) {
      if (group.length < 2) continue
      // 日付ソート
      group.sort((a, b) => a.date.localeCompare(b.date))
      // いずれかのペアが近接 or 同対象月なら重複候補
      let isDup = false
      for (let i = 0; i < group.length && !isDup; i++) {
        for (let j = i + 1; j < group.length; j++) {
          const a = group[i], b = group[j]
          // 同対象月
          if (a.targetMonth && b.targetMonth && a.targetMonth === b.targetMonth) { isDup = true; break }
          // 日付±7日以内
          const da = new Date(a.date).getTime()
          const db = new Date(b.date).getTime()
          if (!isNaN(da) && !isNaN(db)) {
            const diff = Math.abs(da - db) / 86400000
            if (diff <= 7) { isDup = true; break }
          }
        }
      }
      if (isDup) groups.push(group)
    }
    // 金額の大きい順（= 影響の大きい順）にソート
    groups.sort((a, b) => b[0].amount - a[0].amount)
    return groups
  }, [entries])

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-gray-200 bg-amber-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-bold text-amber-900">
              重複候補 {duplicateGroups.length} グループ
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 transition"
            title="閉じる"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 py-2 text-[11px] text-gray-600 border-b border-gray-100 bg-gray-50">
          同じ取引先・同じ金額で、請求日が±7日以内または同対象月の取引を抽出（定期取引は対象外）。
          現在の絞り込み期間（直近{periodDays}日）内の取引が対象。
        </div>
        <div className="flex-1 overflow-auto p-3">
          {duplicateGroups.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400">
              重複候補は見つかりませんでした
            </div>
          ) : (
            <div className="space-y-3">
              {duplicateGroups.map((group, gi) => (
                <div key={gi} className="border border-amber-200 rounded overflow-hidden">
                  <div className="px-3 py-1.5 bg-amber-50 text-[11px] font-medium text-amber-900 flex items-center justify-between">
                    <span>
                      {group[0].vendorName} / {group[0].type === 'expense' ? '−' : ''}¥{group[0].amount.toLocaleString()}
                    </span>
                    <span className="text-amber-700 tabular-nums">{group.length}件</span>
                  </div>
                  <table className="w-full text-[11px]">
                    <thead className="bg-gray-50">
                      <tr className="text-gray-600">
                        <th className="px-2 py-1 text-left font-medium">請求日</th>
                        <th className="px-2 py-1 text-left font-medium">{DUE_DATE_COLUMN_LABEL}</th>
                        <th className="px-2 py-1 text-left font-medium">対象月</th>
                        <th className="px-2 py-1 text-left font-medium">メモ</th>
                        <th className="px-2 py-1 text-left font-medium">登録者</th>
                        <th className="px-2 py-1 text-left font-medium">状態</th>
                        <th className="px-2 py-1 text-center font-medium">操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.map((r) => (
                        <tr key={r.key} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-2 py-1 tabular-nums">{r.date}</td>
                          <td className="px-2 py-1 tabular-nums">{r.dueDate || '—'}</td>
                          <td className="px-2 py-1 tabular-nums">{r.targetMonth || '—'}</td>
                          <td className="px-2 py-1 text-gray-600 max-w-[160px] truncate" title={r.note}>{r.note || '—'}</td>
                          <td className="px-2 py-1 text-gray-700">{r.updatedByName || '—'}</td>
                          <td className="px-2 py-1">
                            {r.confirmedByName ? (
                              <span className="inline-flex items-center gap-0.5 text-green-700">
                                <CheckCircle2 className="w-3 h-3" />確認済
                              </span>
                            ) : (
                              <span className="text-gray-400">未確認</span>
                            )}
                          </td>
                          <td className="px-2 py-1 text-center">
                            <div className="inline-flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => { onClose(); onEdit(r) }}
                                className="inline-flex items-center justify-center w-6 h-6 text-amber-700 hover:bg-amber-100 rounded transition"
                                title="編集"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                onClick={async () => { await onDelete(r) }}
                                className="inline-flex items-center justify-center w-6 h-6 text-red-600 hover:bg-red-100 rounded transition"
                                title="削除"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 text-xs bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded transition"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  )
}
